import apiClient from './api'

export interface UserActivityFilters {
  msisdn?: string
  externalId?: string
  channel?: string
  paybill?: string
  startDate?: string
  endDate?: string
}

export interface UserActivityResponse {
  totals: {
    deposited: number
    withdrawn: number
    net: number
    count: number
  }
  deposits: any[]
  withdrawals: any[]
  timeline: Array<{
    date: string
    amount: number
    type: string
  }>
  behavioral?: {
    by_day: Record<string, { deposits: number; withdrawals: number; count: number; amount: number }>
    by_hour: Record<string, { deposits: number; withdrawals: number; count: number; amount: number }>
    by_weekday: Record<string, { deposits: number; withdrawals: number; count: number; amount: number }>
    statistics: {
      average_deposit: number
      average_withdrawal: number
      max_deposit: number
      max_withdrawal: number
      min_deposit: number
      min_withdrawal: number
      average_transaction: number
      transaction_frequency: number
    }
    most_active: {
      day: string | null
      hour: number | null
      weekday: string | null
    }
  }
}

export interface ResolvedMsisdn {
  msisdn: string
  transactionCount: number
  lastActivity: string | null
  depositsCount?: number
  directDepositsCount?: number
  withdrawalsCount?: number
  totalDeposited?: number
  totalWithdrawn?: number
  netAmount?: number
  paybills?: string[]
  externalUserIds?: string[]
}

export const userAnalyticsService = {
  searchOptions: async (): Promise<string[]> => [],
  resolveMsisdn: (query: string, limit = 20): Promise<ResolvedMsisdn[]> =>
    apiClient
      .get('/users/resolve-msisdn', {
        params: { q: query, limit },
      })
      .then((res) => res.data.items || []),
  fetchActivity: (filters: UserActivityFilters & { msisdn: string }): Promise<UserActivityResponse> =>
    apiClient
      .get(`/users/${filters.msisdn}/activity`, {
        params: {
          start: filters.startDate,
          end: filters.endDate,
          channel: filters.channel,
          paybill: filters.paybill,
        },
      })
      .then((res) => res.data),
  fetchInactiveUsers: (filters: {
    period?: 7 | 30 | 365
    startDate?: string
    endDate?: string
    depositThreshold?: number
    depositIntervalStart?: string
    depositIntervalEnd?: string
  }) => {
    const params: Record<string, any> = {}
    
    if (filters.period) {
      const periodParam = filters.period === 7 ? '7d' : filters.period === 30 ? '30d' : '365d'
      params.period = periodParam
    }
    
    if (filters.startDate) params.start_date = filters.startDate
    if (filters.endDate) params.end_date = filters.endDate
    if (filters.depositThreshold !== undefined) params.deposit_threshold = filters.depositThreshold
    if (filters.depositIntervalStart) params.deposit_interval_start = filters.depositIntervalStart
    if (filters.depositIntervalEnd) params.deposit_interval_end = filters.depositIntervalEnd
    
    return apiClient.get('/users/inactive', { params }).then((res) => res.data.items || [])
  },
  fetchRiskAnalysis: (msisdn: string, startDate?: string, endDate?: string): Promise<UserRiskAnalysisResponse> =>
    apiClient
      .get(`/users/${msisdn}/risk-analysis`, {
        params: {
          start: startDate,
          end: endDate,
        },
      })
      .then((res) => res.data),
}

export interface UserRiskAnalysisResponse {
  msisdn: string
  isBlacklisted: boolean
  overallRiskScore: number
  overallRiskLevel: 'low' | 'medium' | 'high' | 'critical'
  riskMetrics: {
    totalRiskScore: number
    averageRiskScore: number
    maxRiskScore: number
    riskLevelCounts: {
      low?: number
      medium?: number
      high?: number
      critical?: number
    }
    totalTransactions: number
    riskyTransactions: number
  }
  riskFlags: Record<string, number>
  triggeredRules: Record<string, number>
  behavioralRisks: Array<{
    type: string
    severity: 'low' | 'medium' | 'high' | 'critical'
    description: string
    count: number
  }>
  transactionRisks: Array<{
    transactionId: string
    type: string
    amount: number
    date: string
    riskScore: number
    riskLevel: string
    flags: Array<string | {
      flag: string
      description: string
      ruleName: string
      riskScore: number
    }>
    triggeredRules: string[]
  }>
  deposits: any[]
  withdrawals: any[]
  period: {
    start: string
    end: string
  }
}


